<template>
  <div class="home">
    <header class="header">
      <h1>PRAKTIKUM VIRTUAL SISTEM TELEKOMUNIKASI</h1>
      <h2>Laboratorium Virtual Elektronika</h2>
      <p>Platform pembelajaran interaktif untuk praktikum elektronika</p>
    </header>

    <main class="main-content">
      <div class="lab-grid">
        <!-- Card High Pass Filter -->
        <div
          class="lab-card"
          @click="navigateTo('/highpass')"
          data-tippy-content="Klik untuk membuka simulasi High Pass Filter Aktif"
        >
          <div class="card-icon">
            <i class="fas fa-arrow-up-wide-short"></i>
          </div>
          <div class="card-content">
            <h2>High Pass Filter</h2>
            <p>Simulasi dan analisis rangkaian High Pass Filter Aktif</p>
            <ul class="features">
              <li>Visualisasi respons frekuensi</li>
              <li>Analisis gain dan cut-off frequency</li>
              <li>Simulasi sinyal input dan output</li>
            </ul>
          </div>
        </div>

        <!-- Card Low Pass Filter -->
        <div
          class="lab-card"
          @click="navigateTo('/lowpass')"
          data-tippy-content="Klik untuk membuka simulasi Low Pass Filter Aktif"
        >
          <div class="card-icon">
            <i class="fas fa-arrow-down-wide-short"></i>
          </div>
          <div class="card-content">
            <h2>Low Pass Filter</h2>
            <p>Simulasi dan analisis rangkaian Low Pass Filter Aktif</p>
            <ul class="features">
              <li>Visualisasi respons frekuensi</li>
              <li>Analisis gain dan cut-off frequency</li>
              <li>Simulasi sinyal input dan output</li>
            </ul>
          </div>
        </div>

        <!-- Card Amplitude Modulation -->
        <div
          class="lab-card"
          @click="navigateTo('/ammodulation')"
          data-tippy-content="Klik untuk membuka simulasi Amplitude Modulation (AM)"
        >
          <div class="card-icon">
            <i class="fas fa-wave-square"></i>
          </div>
          <div class="card-content">
            <h2>Amplitude Modulation</h2>
            <p>Simulasi dan analisis rangkaian</p>
          </div>
        </div>

        <!-- Card Frequency Modulation -->
        <div
          class="lab-card"
          @click="navigateTo('/freqmod')"
          data-tippy-content="Klik untuk membuka simulasi Frequency Modulation (FM)"
        >
          <div class="card-icon">
            <i class="fas fa-broadcast-tower"></i>
          </div>
          <div class="card-content">
            <h2>Frequency Modulation</h2>
            <p>Simulasi dan analisis rangkaian</p>
          </div>
        </div>

        <!-- Card Analog to Digital Converter -->
        <div
          class="lab-card"
          @click="navigateTo('/adConverter')"
          data-tippy-content="Klik untuk membuka simulasi konversi Analog ke Digital (ADC)"
        >
          <div class="card-icon">
            <i class="fas fa-arrow-right-arrow-left"></i>
          </div>
          <div class="card-content">
            <h2>Analog to Digital Converter (ADC)</h2>
            <p>
              Simulasi proses konversi sinyal analog menjadi digital
              (kuantisasi, encoding, visualisasi NRZ).
            </p>
          </div>
        </div>

        <!-- Card Digital to Analog Converter -->
        <div
          class="lab-card"
          @click="navigateTo('/daConverter')"
          data-tippy-content="Klik untuk membuka simulasi konversi Digital ke Analog (DAC)"
        >
          <div class="card-icon">
            <i class="fas fa-exchange-alt"></i>
          </div>
          <div class="card-content">
            <h2>Digital to Analog Converter (DAC)</h2>
            <p>
              Simulasi proses konversi sinyal digital (biner) menjadi analog
              (rekonstruksi, rekuantisasi).
            </p>
          </div>
        </div>

        <!-- Card Radio Frequency Oscillator (Coming Soon) -->
        <div
          class="lab-card coming-soon"
          data-tippy-content="Modul Radio Frequency Oscillator sedang dikembangkan"
        >
          <div class="card-icon">
            <i class="fas fa-satellite-dish"></i>
          </div>
          <div class="card-content">
            <h2>Radio Frequency Oscillator</h2>
            <p>Simulasi dan analisis rangkaian</p>
            <span class="coming-soon-badge">Coming Soon</span>
          </div>
        </div>

        <!-- Card Band Pass Filter (Coming Soon) -->
        <div
          class="lab-card coming-soon"
          data-tippy-content="Modul Band Pass Filter sedang dikembangkan"
        >
          <div class="card-icon">
            <i class="fas fa-sliders"></i>
          </div>
          <div class="card-content">
            <h2>Band Pass Filter</h2>
            <p>Simulasi dan analisis rangkaian</p>
            <span class="coming-soon-badge">Coming Soon</span>
          </div>
        </div>

        <!-- Card Band Stop Filter (Coming Soon) -->
        <div
          class="lab-card coming-soon"
          data-tippy-content="Modul Band Stop Filter sedang dikembangkan"
        >
          <div class="card-icon">
            <i class="fas fa-ban"></i>
          </div>
          <div class="card-content">
            <h2>Band Stop Filter</h2>
            <p>Simulasi dan analisis rangkaian</p>
            <span class="coming-soon-badge">Coming Soon</span>
          </div>
        </div>
      </div>
    </main>

    <footer class="footer">
      <p>&copy; 2025 Laboratorium Virtual Elektronika. All rights reserved.</p>
    </footer>
  </div>
</template>

<script>
/**
 * Halaman beranda utama
 * Menampilkan kartu navigasi ke setiap modul praktikum dan tooltip penjelasan
 */
import { useRouter } from "vue-router";
import { onMounted } from "vue";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/scale.css";

export default {
  name: "HomeView",
  setup() {
    const router = useRouter();

    const navigateTo = (path) => {
      router.push(path);
    };

    onMounted(() => {
      tippy("[data-tippy-content]", {
        animation: "scale",
        duration: 200,
        theme: "light-border",
      });
    });

    return {
      navigateTo,
    };
  },
};
</script>

<style scoped>
.home {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #1a1a1a;
}

.header {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  color: #ffffff;
  padding: 3rem 2rem;
  text-align: center;
  border-bottom: 1px solid #404040;
}

.header h1 {
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
}

.header h2 {
  margin: 0.5rem 0;
  font-size: 1.8rem;
  color: #4a9eff;
}

.header p {
  margin: 1rem 0 0;
  font-size: 1.2rem;
  color: #a0a0a0;
}

.main-content {
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.lab-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 1rem;
}

.lab-card {
  background: #2a2a2a;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid #404040;
}

.lab-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.4);
  border-color: #4a9eff;
}

.lab-card.coming-soon {
  opacity: 0.7;
  cursor: not-allowed;
}

.card-icon {
  font-size: 2.5rem;
  color: #4a9eff;
  margin-bottom: 1rem;
}

.card-content h2 {
  margin: 0 0 0.5rem;
  color: #ffffff;
  font-size: 1.5rem;
}

.card-content p {
  margin: 0 0 1rem;
  color: #a0a0a0;
  font-size: 1rem;
}

.features {
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
}

.features li {
  padding: 0.5rem 0;
  color: #a0a0a0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.features li::before {
  content: "•";
  color: #4a9eff;
  font-weight: bold;
  margin-right: 0.5rem;
}

.coming-soon-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #ff9800;
  color: #ffffff;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.footer {
  background: #2a2a2a;
  color: #a0a0a0;
  text-align: center;
  padding: 1.5rem;
  margin-top: auto;
  border-top: 1px solid #404040;
}

@media (max-width: 768px) {
  .header {
    padding: 2rem 1rem;
  }

  .header h1 {
    font-size: 2rem;
  }

  .main-content {
    padding: 1rem;
  }

  .lab-grid {
    grid-template-columns: 1fr;
  }
}
</style>
